import * as React from 'react';
import { useDiagnosisPermission } from '../../../contexts/DiagnosisPermissionContext';
import { forwardRef, useMemo } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  CircularProgress,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { useQuery } from 'react-query';

import { DiagnosisStepHandle } from './Step.types';
import { diagnosisCloseExportApi, DiagnosisChecklistItem } from '../../../services/apiClient';
import { computeDiagnosisChecklistProgress } from '../../../utils/diagnosisCloseProgress';
import { ActionSpacer } from '../../common';

interface DiagnosisCloseExportStepProps {
  diagnosisId: string;
}

export const DiagnosisCloseExportStep = React.memo(
  forwardRef<DiagnosisStepHandle, DiagnosisCloseExportStepProps>(({ diagnosisId }, ref) => {
    const sectionOrder: string[] = [
      'Diagnosi',
      'Dati impresa',
      'Sito',
      'Dati climatici',
      'Scheda immobile',
      'Impianto autoproduzione',
      'Vettori energetici',
      'Contatori',
      'Variabili pertinenti',
      'Consumi e costi',
      'Bilancio energetico',
      'Grafici POD orari',
      'Indicatori',
      'Usi energetici',
      'Censimenti',
      'Monitoraggio ENEA',
      'Interventi',
      'Gerarchia',
      'Controlli finali',
      'Altro',
    ];
    const sectionOrderMap = new Map(sectionOrder.map((section, index) => [section, index]));

    React.useImperativeHandle(ref, () => ({
      save: async () => ({ success: true }),
    }));

    const { data: finalChecks, isLoading: finalChecksLoading } = useQuery(
      ['diagnosis-final-checks', diagnosisId],
      () => diagnosisCloseExportApi.getFinalChecks(diagnosisId)
    );

    const { data: requiredFields, isLoading: requiredFieldsLoading } = useQuery(
      ['diagnosis-required-fields', diagnosisId],
      () => diagnosisCloseExportApi.getRequiredFieldsChecklist(diagnosisId)
    );

    const groupBySection = (items: DiagnosisChecklistItem[]) =>
      items.reduce<Record<string, DiagnosisChecklistItem[]>>((acc, item) => {
        const section = item.section || 'Altro';
        if (!acc[section]) acc[section] = [];
        acc[section].push(item);
        return acc;
      }, {});

    const groupedFinalChecks = useMemo(
      () => groupBySection(finalChecks?.checks ?? []),
      [finalChecks?.checks]
    );
    const groupedRequiredFields = useMemo(
      () => groupBySection(requiredFields?.checks ?? []),
      [requiredFields?.checks]
    );

    const progress = useMemo(
      () => computeDiagnosisChecklistProgress(requiredFields, finalChecks),
      [requiredFields, finalChecks]
    );

    const getCountColor = (passed: number, total: number) =>
      passed === total ? 'success.main' : 'warning.main';

    const renderGroupedAccordions = (
      grouped: Record<string, DiagnosisChecklistItem[]>,
      detailsOnRight = false
    ) =>
      Object.entries(grouped)
        .sort(([sectionA], [sectionB]) => {
          const indexA = sectionOrderMap.get(sectionA) ?? Number.MAX_SAFE_INTEGER;
          const indexB = sectionOrderMap.get(sectionB) ?? Number.MAX_SAFE_INTEGER;
          if (indexA !== indexB) return indexA - indexB;
          return sectionA.localeCompare(sectionB);
        })
        .map(([section, items]) => {
          const passedCount = items.filter(item => item.passed).length;
          const totalCount = items.length;

          return (
            <Accordion key={section}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ width: '100%', pr: 1 }}
                >
                  <Typography fontWeight={600}>{section}</Typography>
                  <Typography
                    fontWeight={700}
                    sx={{ color: getCountColor(passedCount, totalCount) }}
                  >
                    {passedCount}/{totalCount}
                  </Typography>
                </Stack>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={1}>
                  {items.map(item => (
                    <Stack
                      key={item.code}
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      sx={{ gap: 1 }}
                    >
                      <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
                        {item.passed ? (
                          <CheckCircleIcon color="success" fontSize="small" />
                        ) : (
                          <ErrorOutlineIcon color="warning" fontSize="small" />
                        )}
                        <Typography variant="body2">{item.label}</Typography>
                      </Stack>
                      {detailsOnRight && item.details ? (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ textAlign: 'right', ml: 2, flexShrink: 0 }}
                        >
                          {item.details}
                        </Typography>
                      ) : !detailsOnRight && item.details ? (
                        <Typography variant="body2" color="text.secondary">
                          ({item.details})
                        </Typography>
                      ) : null}
                    </Stack>
                  ))}
                </Stack>
              </AccordionDetails>
            </Accordion>
          );
        });

    const isLoading = finalChecksLoading || requiredFieldsLoading;

    return (
      <Stack spacing={2} sx={{ p: 2 }}>
        <Typography variant="h6">Chiudi ed esporta</Typography>

        {isLoading ? (
          <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography fontWeight={700} sx={{ mb: 1 }}>
                Controlli finali
              </Typography>
              {renderGroupedAccordions(groupedFinalChecks, true)}
            </Paper>

            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography fontWeight={700} sx={{ mb: 1 }}>
                Campi obbligatori
              </Typography>
              {renderGroupedAccordions(groupedRequiredFields)}
            </Paper>

            <Paper variant="outlined" sx={{ p: 2 }}>
              <Stack spacing={1}>
                <Typography fontWeight={600}>Stato avanzamento</Typography>
                <Typography variant="body2">{progress}% completato</Typography>
                <LinearProgress variant="determinate" value={progress} />
              </Stack>
            </Paper>
          </>
        )}
        <ActionSpacer></ActionSpacer>
      </Stack>
    );
  })
);

DiagnosisCloseExportStep.displayName = 'DiagnosisCloseExportStep';
